import type React from "react";
import { useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { useLocation } from "react-router-dom";
import { tokens } from "../../../theme";
import MuzukuruLogo from "../../../assets/Muzukuruimg1.png";
import backgroundPattern from "../../../assets/backgroundPattern.jpg";
import { Download } from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

const StreamReport: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { state } = useLocation();
  const deceasedName = state?.deceasedName || "N/A";
  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const PIE_DATA = [
    { id: 0, value: 50.2, label: "Zimbabwe", color: colors.primary[500] },
    { id: 1, value: 15.2, label: "United Kingdom", color: colors.primary[400] },
    { id: 2, value: 14.7, label: "South Africa", color: colors.secondary[400] },
    { id: 3, value: 12.1, label: "Other", color: colors.primary[600] },
    { id: 4, value: 7.8, label: "Australia", color: colors.secondary[800] },
  ];

  const VIEWS_DATA = [
    { category: "At Venue", value: 180 },
    { category: "Online", value: 120 },
  ];

  const COMMENTS_DATA = [
    { category: "Muzukuru Website", value: 25 },
    { category: "Tribute Table", value: 8 },
  ];

  const totalViews = VIEWS_DATA.reduce((sum, item) => sum + item.value, 0);
  const totalComments = COMMENTS_DATA.reduce((sum, item) => sum + item.value, 0);

  const handleDownload = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const imgData = canvas.toDataURL("image/png");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Livestream-Report-${deceasedName}.pdf`);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ width: `${A4_WIDTH}px`, margin: "0 auto", p: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Tooltip title="Download PDF">
          <IconButton
            onClick={handleDownload}
            disabled={downloading}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.primary[900],
              width: 40,
              height: 40,
              "&:hover": { backgroundColor: colors.secondary[400] },
            }}
          >
            {downloading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <Download />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        ref={printRef}
        elevation={4}
        sx={{
          width: `${A4_WIDTH}px`,
          minHeight: `${A4_HEIGHT}px`,
          p: "12mm 10mm",
          pt: "20mm",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: colors.primary[900],
          backgroundImage: `url(${backgroundPattern})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          transform: isMobile ? "scale(0.45)" : "scale(1)",
          transformOrigin: "top center",
          "@media print": {
            boxShadow: "none",
            width: "210mm",
            minHeight: "297mm",
            p: "12mm 14mm",
            transform: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            mb: "8mm",
            pb: "5mm",
          }}
        >
          <Box
            component="img"
            src={MuzukuruLogo}
            alt="Muzukuru Logo"
            sx={{
              width: 100,
              height: 100,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              color: colors.primary[400],
              fontSize: "32px",
              lineHeight: 1.1,
              textTransform: "uppercase",
              fontFamily: "'Playfair Display', serif",
              letterSpacing: "0.5px",
            }}
          >
            Livestream Performance
            <br />
            Report
          </Typography>
        </Box>

        <Typography
          sx={{
            color: colors.primary[400],
            mb: "7mm",
            fontSize: "20px",
            textAlign: "left",
            fontWeight: 700,
            fontFamily: "sans-serif",
            alignSelf: "flex-start",
          }}
        >
          {`This report gives an insight on how the livestream was viewed for ${deceasedName.endsWith("s") ? `${deceasedName}'` : `${deceasedName}'s`} Funeral Service.`}
        </Typography>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            mb: "6mm",
          }}
        >
          <PieChart
            series={[
              {
                data: PIE_DATA,
                innerRadius: 0,
                outerRadius: 130,
                paddingAngle: 0,
                arcLabel: (item) => `${item.value}%`,
                arcLabelMinAngle: 10,
              },
            ]}
            width={500}
            height={300}
            margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
            slotProps={{
              legend: {
                direction: "horizontal",
                position: { vertical: "bottom", horizontal: "center" },
              },
            }}
          />
        </Box>

        <Typography
          sx={{
            color: colors.primary[400],
            mb: "5mm",
            fontSize: "16px",
            textAlign: "left",
            alignSelf: "flex-start",
            fontWeight: 700,
            fontFamily: "sans-serif",
          }}
        >
          {`People in different locations were able to watch the stream and join you in celebrating ${deceasedName.endsWith("s") ? `${deceasedName}'` : `${deceasedName}'s`} life.`}
        </Typography>

        <Box
          sx={{
            borderTop: `4px dotted ${colors.primary[500]}`,
            width: "100%",
            my: "4mm",
          }}
        />

        <Grid
          container
          spacing={4}
          justifyContent="center"
          sx={{ width: "100%" }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              sx={{
                textAlign: "center",
                mb: 1,
                color: colors.primary[400],
                fontSize: "16px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Views
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "14px",
                color: colors.primary[500],
                mb: 1,
              }}
            >
              Total: {totalViews}
            </Typography>
            <BarChart
              dataset={VIEWS_DATA}
              yAxis={[{ scaleType: "band", dataKey: "category", width: 120 }]}
              series={[
                {
                  dataKey: "value",
                  color: colors.primary[400],
                  valueFormatter: (value) =>
                    value != null
                      ? `${value} (${Math.round((value / totalViews) * 100)}%)`
                      : "0 (0%)",
                },
              ]}
              layout="horizontal"
              height={160}
              width={380}
              margin={{ left: 20, right: 60, top: 10, bottom: 20 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              sx={{
                textAlign: "center",
                mb: 1,
                color: colors.primary[400],
                fontSize: "16px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Comments Received
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "14px",
                color: colors.primary[500],
                mb: 1,
              }}
            >
              Total: {totalComments}
            </Typography>
            <BarChart
              dataset={COMMENTS_DATA}
              yAxis={[{ scaleType: "band", dataKey: "category", width: 120 }]}
              series={[
                {
                  dataKey: "value",
                  color: colors.primary[500],
                  valueFormatter: (value) =>
                    value != null
                      ? `${value} (${Math.round((value / totalComments) * 100)}%)`
                      : "0 (0%)",
                },
              ]}
              layout="horizontal"
              height={160}
              width={380}
              margin={{ left: 20, right: 60, top: 10, bottom: 20 }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            borderTop: `4px dotted ${colors.primary[500]}`,
            width: "100%",
            my: "4mm",
          }}
        />
      </Paper>
    </Box>
  );
};

export default StreamReport;